import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";

/**
 * Hook to acquire an MSAL token (ID token by default using loginRequest.scopes).
 * - Returns { token, loading, error }
 * - Sets Authorization-ready token string (no Bearer prefix) that can be passed to API calls
 */
export default function useApiToken() {
  const { instance, accounts, inProgress } = useMsal();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function getToken() {
      try {
        if (inProgress !== InteractionStatus.None) return; // wait
        if (!accounts || accounts.length === 0) {
          setToken(null);
          return;
        }
        const request = { ...loginRequest, account: accounts[0] };
        const resp = await instance.acquireTokenSilent(request).catch(async (e) => {
          // Fallback to popup when silent fails
          return instance.acquireTokenPopup(request);
        });
        if (cancelled) return;
        // Use ID token by default as per your requirement
        setToken(resp?.idToken || resp?.accessToken || null);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    setLoading(true);
    setError(null);
    getToken();

    return () => {
      cancelled = true;
    };
  }, [instance, accounts, inProgress]);

  return { token, loading, error };
}
